import { exportAllData, importAllData } from '../crypto/data-export.js';
import { buildMapArchive, readMapArchive } from '../../shared/utils/map-archive.js';
import { backupAttachmentReferences } from '../crypto/backup-state.js';
import { getExportAttachment } from './map-export.js';
import { listAttachments } from './attachment-service.js';
import { runOperation } from '../host.js';
import { destinationFolder, storageOf } from '../core/vault-folders.js';
import { t } from '../../shared/i18n/index.js';

/** A full independent copy, including unopened maps, notes, execution and files.
 * Collect and verify every source before allocating a new document.
 * state: copy this content instead of the map's current one (a version from
 * the map's history, engine/history.js); files it shows that were deleted
 * since are reported, not a reason to stop. */
export function copyMap(vault, docId, { title, folderId, state = null } = {}) {
  return runOperation(t('vault.map.duplicate'), async progress => {
    const source = vault.getDoc(docId);
    if (!source) throw new Error(t('vault.copy.unavailable'));
    const { exportData, exportErrors } = await exportAllData({ docIds: [docId], includeAttachments: true });
    if (exportErrors.length) throw new Error(t('vault.copy.unverified'));
    const doc = exportData.documents[0];
    if (!doc?.crdtState) throw new Error(t('vault.copy.noContent'));
    const index = vault.getIndex();
    const sourceFolder = index.folders.find(folder => folder.id === source.folderId && storageOf(folder) === storageOf(source));
    const targetId = folderId || sourceFolder?.id || await vault.ensureRootFolder(storageOf(source));
    const target = destinationFolder(vault.getIndex(), source, targetId);
    doc.name = title || t('vault.map.copyName', { title: source.title || t('common.untitled') });
    doc.folderId = target.id;
    // Reuse the chosen folder, not an imported duplicate of its ancestors.
    exportData.folders = [];
    const { YjsDocManager } = await import('../sync/yjs-doc.js');
    const { Keypair } = await import('../crypto/keypair.js');
    const manager = new YjsDocManager(docId);
    let bytes;
    try {
      manager.applyUpdate(state || Uint8Array.from(atob(doc.crdtState), char => char.charCodeAt(0)));
      const rootId = manager.metadata.get('rootId');
      if (rootId) manager.updateNode(rootId, { name: doc.name });
      bytes = manager.getFullState();
      doc.crdtState = Keypair.toBase64(bytes);
    } finally { manager.destroy(); }
    const refs = [...backupAttachmentReferences(bytes).keys()].map(ref => `attachment:${ref}`);
    const archive = await buildMapArchive({ blob: new Blob([JSON.stringify(exportData)], { type: 'application/json' }),
      filename: 'map.json', documents: [{ id: docId, data: [doc, refs], listAll: storageOf(source) === 'online' }],
      list: id => listAttachments(id), download: getExportAttachment, onActivity: progress.update });
    if (archive.warnings.length && !state) throw new Error(t('vault.copy.attachmentsFailed'));
    const attachmentArchive = await readMapArchive(archive.blob, progress.update);
    return importAllData(exportData, { mergeWithExisting: false, attachmentArchive, keepCurrentDocument: true });
  }, result => [t(result.errors.length || result.syncErrors.length ? 'vault.copy.withWarnings' : 'vault.copy.done'),
    result.errors.length > 0 || result.syncErrors.length > 0]);
}
